// DocuSignStep.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper, 
  TextField, 
  Grid,
  Alert
} from '@mui/material';
import { Check } from '@mui/icons-material';
import { SignupData } from 'form-application-shared';
import { isValidEmail } from '../utils/validators';

interface DocuSignStepProps {
  formData: SignupData;
  setFormData?: React.Dispatch<React.SetStateAction<SignupData>>;
  onValidationChange?: (validationFn: () => boolean) => void;
}

interface RequiredDocument {
  name: string;
  description: string;
  required: boolean;
}

const DocuSignStep: React.FC<DocuSignStepProps> = ({ 
  formData, 
  setFormData,
  onValidationChange 
}) => {
  // Documents required for signing
  const requiredDocuments: RequiredDocument[] = [
    {
      name: "Reinsurance Application",
      description: "Application for reinsurance participation",
      required: formData.hasReinsurance // Only required if reinsurance products are selected
    }
  ];
  
  if (formData.ach && formData.ach.optIn) {
    requiredDocuments.push({
      name: "ACH Authorization",
      description: "Authorization for automatic clearing house transactions",
      required: formData.hasReinsurance // Only required if reinsurance products are selected
    });
  }

  // Filter to get only the required documents based on form state
  const documentsToSign = requiredDocuments.filter(doc => doc.required);

  // State for field validation
  const [errors, setErrors] = useState<{
    signedByName: string;
    signedByEmail: string;
  }>({
    signedByName: '',
    signedByEmail: ''
  });

  // Handle field changes
  const handleChange = (field: keyof SignupData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (setFormData) {
      setFormData({
        ...formData,
        [field]: e.target.value
      });
    }

    // Clear error when typing
    if (errors[field as keyof typeof errors]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  // Validation function wrapped in useCallback to prevent unnecessary recreations
  const validateFields = useCallback((): boolean => {
    // If we don't have setFormData, we're in read-only mode (after submission)
    if (!setFormData) return true;
    
    const newErrors = {
      signedByName: '',
      signedByEmail: ''
    };
    
    let isValid = true;

    // Validate name
    if (!formData.signedByName?.trim()) {
      newErrors.signedByName = 'Name is required';
      isValid = false;
    }

    // Validate email
    if (!formData.signedByEmail?.trim()) {
      newErrors.signedByEmail = 'Email is required';
      isValid = false;
    } else if (!isValidEmail(formData.signedByEmail)) {
      newErrors.signedByEmail = 'Please enter a valid email address';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }, [formData, setFormData]);

  // Register validation function with parent component
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateFields);
    }
  }, [validateFields, onValidationChange]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        DocuSign
      </Typography>
      
      {/* Signatory Information Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Authorized Signatory
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Please provide the name and email of the person authorized to sign the application documents.
        </Alert>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Authorized Signatory Legal Name"
              value={formData.signedByName || ''}
              onChange={handleChange('signedByName')}
              error={!!errors.signedByName}
              helperText={errors.signedByName}
              required
              margin="normal"
              disabled={!setFormData} // Disable if in read-only mode
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Authorized Signatory Email"
              value={formData.signedByEmail || ''}
              onChange={handleChange('signedByEmail')}
              error={!!errors.signedByEmail}
              helperText={errors.signedByEmail}
              required
              margin="normal"
              type="email"
              disabled={!setFormData} // Disable if in read-only mode
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Typography variant="body1" paragraph>
        The following documents will be sent to you via DocuSign for electronic signature:
      </Typography>
      
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <List>
          {documentsToSign.map((document, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Check color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={document.name}
                secondary={document.description}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
      
      <Typography variant="body2" color="textSecondary">
        Once you complete this form, you will receive an email from DocuSign with instructions to sign these documents electronically.
      </Typography>
    </Box>
  );
};

export default DocuSignStep;